import { Component } from '@theme/component';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The contact form's own validation summary (build plan, 12 Sept 2026): "an error summary that
 * takes focus, with each field's error in words; the thanks takes focus."
 *
 * Shopify's contact form only ever validates the e-mail field server-side, so an empty name or
 * message needs client-side validation to be caught at all, in words, per field. Progressive
 * enhancement: with JavaScript off, the fields are still `required` (the browser's own bubble
 * stops an empty submit) and a bad e-mail still round-trips to Liquid's own error markup, which
 * already carries `autofocus`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} summary
 * @property {HTMLUListElement} summaryList
 * @property {HTMLInputElement[]} [fields]
 * @property {HTMLElement[]} [fieldErrors]
 *
 * @extends {Component<Refs>}
 */
export class ContactFormValidationComponent extends Component {
  requiredRefs = ['summary', 'summaryList'];

  /** Bound to the form via on:submit in the Liquid; the form is the event's own target. */
  handleSubmit(event) {
    const fields = this.refs.fields ?? [];
    const fieldErrors = this.refs.fieldErrors ?? [];
    const problems = [];

    for (const field of fields) {
      const key = field.dataset.field;
      const errorEl = fieldErrors.find((e) => e.dataset.field === key);
      const message = this.#validate(field);

      if (message) {
        problems.push({ key, field, message });
        field.setAttribute('aria-invalid', 'true');
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
      } else {
        field.removeAttribute('aria-invalid');
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
      }
    }

    if (problems.length === 0) {
      this.refs.summary.hidden = true;
      return; // let the real Shopify submit proceed
    }

    event.preventDefault();
    this.#renderSummary(problems);
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {string} the message to show, or '' when the field is valid
   */
  #validate(field) {
    const key = field.dataset.field ?? '';
    const value = field.value.trim();
    const label = key.charAt(0).toUpperCase() + key.slice(1);
    const error = this.dataset[`error${label}`] ?? '';

    if (key === 'email') return !value || !EMAIL.test(value) ? error : '';
    return value ? '' : error;
  }

  /** @param {{ key: string, field: HTMLElement, message: string }[]} problems */
  #renderSummary(problems) {
    const { summary, summaryList } = this.refs;

    summaryList.replaceChildren(
      ...problems.map(({ key, field, message }) => {
        const label = key.charAt(0).toUpperCase() + key.slice(1);
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'link';
        link.href = `#${field.id}`;
        link.textContent = `${this.dataset[`label${label}`] ?? key}: ${message}`;
        link.addEventListener('click', (linkEvent) => {
          linkEvent.preventDefault();
          field.focus();
        });
        item.append(link);
        return item;
      }),
    );

    summary.hidden = false;
    summary.focus();
  }
}

if (!customElements.get('contact-form-validation-component')) {
  customElements.define('contact-form-validation-component', ContactFormValidationComponent);
}
